import React, {FC, RefObject} from "react";
import {FilePO} from "@/model/content";
import {GridActionsCellItem, GridColDef} from "@mui/x-data-grid";
import {checkFileType, formatFileSize, formatTime} from "@/helper/method";
import {ProTable} from "@/widget/ProTable/ProTable";
import {downloadFile, useDeleteFile, useQueryFilePage} from "@/service/file.s";
import {get} from "lodash";
import Button from "@mui/material/Button";
import {CloudUploadOutlined, InsertLinkOutlined, FileDownloadOutlined, SearchOutlined} from "@mui/icons-material";
import {WriteFile, WriteFileType} from "@/widget/UploadFile/WriteFile";
import {useGlobalActions} from "@/store/global.c";
import {DictPO} from "@/model/system";
import NoFilesOverlay from "@/widget/NoResultsOverlay/NoFilesOverlay";
import Stack from "@mui/material/Stack";
import {DeleteOutlineOutlined} from '@mui/icons-material';
import Typography from "@mui/material/Typography";
import {GridApiCommunity} from "@mui/x-data-grid/internals";
import {mediaType} from "@/helper/constant";
import isEmpty from "lodash/isEmpty";

const {VITE_WEBSITE} = import.meta.env;

export interface FileTableProps {
    targetId?: number
    tag?: number;
    checkboxSelection?: boolean;
    options?: DictPO[]
    disabled?: boolean;
    actionable?: boolean;
}

export const FileTable: FC<FileTableProps> = ({tag, targetId, disabled = false, options, checkboxSelection, actionable = true}) => {
    const {mutateAsync: filePageMutate, data: filePageData, isPending: filePagePending} = useQueryFilePage()
    const {setDialogModal, setToast} = useGlobalActions()
    const {mutateAsync: deleteFileMutate} = useDeleteFile()

    const columns: GridColDef<FilePO>[] = [
        {
            field: "name",
            headerName: "文件名称",
            headerAlign: "left",
            align: "left",
            flex: 0.6
        },
        {
            field: "type",
            headerName: "文件类型",
            flex: 0.5,
            renderCell: ({row}) => {
                return row?.extra?.category
            }
        },
        {
            field: "size",
            headerName: "大小",
            flex: 0.4,
            valueFormatter: value => formatFileSize(value)
        },
        {
            field: "createdAt",
            headerName: "上传日期",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            valueFormatter: (value) => formatTime("D", value),
        }, {
            field: "actions",
            type: "actions",
            flex: 0.55,
            headerName: "操作",
            getActions: ({row: {url, id, name, type, images}}) => {
                const link: string = `${VITE_WEBSITE}/preview?id=${id}`
                return [<GridActionsCellItem
                    icon={<FileDownloadOutlined/>}
                    label="下载"
                    color={'default'}
                    onClick={() => {
                        downloadFile({url, filename: name})
                    }}
                />, <GridActionsCellItem
                    icon={<InsertLinkOutlined/>}
                    label="复制链接"
                    color={'default'}
                    onClick={() => {
                        if (checkFileType(mediaType['image'], type)) {
                            navigator.clipboard.writeText(link)
                        } else {
                            navigator.clipboard.writeText(url)
                        }
                        setToast({open: true, message: "复制链接成功", variant: 'standard', severity: 'success'})
                    }}
                />, <GridActionsCellItem
                    icon={<SearchOutlined/>}
                    label="查看"
                    color={'default'}
                    onClick={() => {
                        if (checkFileType(mediaType['image'], type) || !isEmpty(images)) {
                            window.open(link)
                        } else {
                            setToast({open: true, message: "文件不支持预览，请下载查看", duration: 5000, variant: 'standard', severity: 'warning'})
                        }
                    }}
                />]
            }
        }]

    const onUploadFile = ({title, tag, onSearch, options}: WriteFileType) => {
        setDialogModal({header: title, maxWidth: 'xs', body: <WriteFile targetId={targetId!} options={options} tag={tag}/>, open: true, onSearch})
    }

    const onDeleteFile = async (apiRef: RefObject<GridApiCommunity | null>) => {
        const selectedRows = apiRef?.current?.getSelectedRows();
        if (selectedRows?.size != 1) {
            setToast({open: true, message: "请选择一条数据", severity: "warning", variant: 'standard'})
            return
        }
        const row = selectedRows?.entries()?.next()
        const id = get(row, 'value[0]', 0) as number
        const result = await deleteFileMutate({
            id
        });
        if (result.code == 0) {
            await refetchData()
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    const refetchData = async () => {
        await filePageMutate({current: 1, size: 5, targetId, tag: tag})
    }

    return <ProTable<FilePO> checkboxSelection={!disabled && checkboxSelection} columnHeaderHeight={40} loading={filePagePending}
                             onSearch={(params) => filePageMutate({...params, targetId, tag})}
                             data={filePageData?.data} pageSize={5} showToolbar={false}
                             columns={columns}
                             noResults={() => <NoFilesOverlay onClick={() => {
                                 !disabled && onUploadFile({
                                     title: '上传文件', options: options || [], tag:
                                         tag!, onSearch:
                                         () => refetchData()
                                 })
                             }}/>}
                             autoHeight={false}
                             feet={{
                                 hidden: get(filePageData, "data.total", 0) == 0,
                                 custom: true,
                                 leftFunc: (apiRef: RefObject<GridApiCommunity | null>) => {
                                     if (!actionable) {
                                         return <></>
                                     }
                                     return !disabled && <Stack direction={'row'} spacing={1.3} width={'100%'}>
                                         <Button
                                             onClick={() => {
                                                 onUploadFile({
                                                     title: '上传文件',
                                                     tag: tag!,
                                                     options: options || [],
                                                     onSearch: () => refetchData()
                                                 })
                                             }}
                                             disabled={disabled}
                                             startIcon={<CloudUploadOutlined color={'info'}/>} variant={'outlined'}
                                             size={'small'}
                                             color={'inherit'}><Typography color={'info'} variant={'caption'}>上传文件</Typography></Button>
                                         <Button
                                             onClick={async () => {
                                                 await onDeleteFile(apiRef)
                                             }}
                                             startIcon={<DeleteOutlineOutlined color={'error'}/>} variant={'outlined'}
                                             size={'small'}
                                             disabled={disabled}
                                             color={'error'}><Typography color={'error'} variant={'caption'}>刪除文件</Typography></Button>
                                     </Stack>
                                 }
                             }}
    />
}